import { format } from "date-fns";

const toDateEnd = (date: Date) => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

export interface IPeriodFilter {
  startFrom?: Date | null;
  startTo?: Date | null;
}

export interface ISelectFilter {
  value: string | null;
  label: string;
}

export const matchesPeriod = (
  dateValue: string,
  filter: IPeriodFilter,
): boolean => {
  const date = new Date(dateValue);

  if (filter.startFrom) {
    const from = new Date(filter.startFrom);
    if (date < from) return false;
  }

  if (filter.startTo) {
    const to = toDateEnd(new Date(filter.startTo));
    if (date > to) return false;
  }

  return true;
};

export const matchesIsoDateRange = (
  dateValue: string | undefined,
  from?: string,
  to?: string,
): boolean => {
  if (!from && !to) return true;
  if (!dateValue) return false;

  const date = new Date(dateValue).getTime();
  if (!Number.isFinite(date)) return false;
  if (from && date < new Date(`${from}T00:00:00.000Z`).getTime()) return false;
  if (to && date > new Date(`${to}T23:59:59.999Z`).getTime()) return false;
  return true;
};

export const matchesStatus = (
  status: string,
  filter?: ISelectFilter | null,
) => {
  if (!filter?.value) return true;
  return status === filter.value;
};

export const matchesSelect = (value: string, filter?: ISelectFilter | null) => {
  if (!filter?.value) return true;
  return value === filter.value;
};

export const matchesText = (haystack: string, needle?: string) => {
  if (!needle) return true;
  return haystack.toLowerCase().includes(needle.toLowerCase());
};

export const toDate = (value: unknown): Date | undefined => {
  if (!value) return undefined;
  if (typeof (value as { toDate?: () => Date }).toDate === "function") {
    return (value as { toDate: () => Date }).toDate();
  }
  return value as Date;
};

export const getSelectDefault = (
  options: { value: string; label: string }[],
  current?: ISelectFilter | null,
) => {
  if (!current?.value) return options[0];
  return current;
};

export const getSelectDefaultByValue = (
  options: { value: string; label: string }[],
  current?: string,
) => options.find((option) => option.value === current) || options[0];

export const toDateString = (value: unknown): string | null => {
  if (!value) return null;
  if (typeof value === "string") return value;
  const date = toDate(value);
  return date ? format(date, "yyyy-MM-dd") : null;
};

export const toApiDateStart = (date?: Date | null) => {
  const value = toDateString(date);
  return value ? `${value}T00:00:00.000Z` : undefined;
};

export const toApiDateEnd = (date?: Date | null) => {
  const value = toDateString(date);
  return value ? `${value}T23:59:59.999Z` : undefined;
};

export const parseDate = (value?: string | null): Date | null =>
  value ? new Date(value) : null;

export const splitCsv = (value?: string | null): string[] =>
  value ? String(value).split(",").filter(Boolean) : [];

export const joinCsv = (values?: string[]): string | undefined =>
  values?.length ? values.join(",") : undefined;

export const toSelectFilter = (
  value?: string | null,
  label?: string | null,
): ISelectFilter | null => (value ? { value, label: label || value } : null);

export const optionalText = (value?: string | null): string | undefined =>
  value || undefined;

export const parsePeriodFromUrl = (
  from?: string,
  to?: string,
): IPeriodFilter => ({
  startFrom: from ? new Date(from) : null,
  startTo: to ? new Date(to) : null,
});

export const filterItem = (obj, predicate) =>
  Object.keys(obj)
    .filter((key) => predicate(key))
    .reduce(
      (res, key) =>
        Object.assign(res, { [key]: !!obj[key] ? obj[key] : undefined }),
      {},
    );

export const filterOptionByLabel = (
  input: string,
  option?: { label?: unknown },
) =>
  String(option?.label ?? "")
    .toLowerCase()
    .includes(input.toLowerCase());
